import {
	ProductReviewListInput,
	type ProductReviewListItem,
	type ProductReviewListQuery,
	ProductReviewStatus,
} from "@cleanbudget/contract/product-review-admin";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { DataTableEmpty } from "@/components/data-table/empty";
import { DataTablePagination } from "@/components/data-table/pagination";
import { DataTableToolbar, FilterSelect, SearchInput } from "@/components/data-table/toolbar";
import { PageHeader } from "@/components/page-header";
import { formatDateTime } from "@/lib/format";
import { orpc } from "@/lib/orpc";
import { ReviewStatusBadge, Stars } from "./-components/review-bits";

const reviewsQuery = (query: ProductReviewListQuery) =>
	orpc.productReviewAdmin.list.queryOptions({ input: query });

/** The list's defaults, left out of the URL so a plain `/product-review` is the default view. */
const { sort, order, page, pageSize } = ProductReviewListInput.parse({});

/**
 * Reviews (#260, stories 118 and 120): every review, the newest first, filtered on status (te
 * beoordelen, gepubliceerd, afgewezen) and product. A row links to the review, where it is judged.
 */
export const Route = createFileRoute("/_admin/product-review/")({
	validateSearch: ProductReviewListInput,
	search: { middlewares: [stripSearchParams({ sort, order, page, pageSize })] },
	loaderDeps: ({ search }) => search,
	loader: ({ context, deps }) => context.queryClient.ensureQueryData(reviewsQuery(deps)),
	component: ReviewsPage,
});

function ReviewsPage() {
	const { t } = useTranslation("productReview");
	const search = Route.useSearch();
	const { data } = useSuspenseQuery(reviewsQuery(search));

	const columns: readonly Column<ProductReviewListItem>[] = [
		{
			id: "review",
			header: t("column.review"),
			cell: (review) => (
				<span className="flex max-w-xs flex-col gap-1">
					<Link
						to="/product-review/$reviewId"
						params={{ reviewId: review.id }}
						className="font-medium underline-offset-4 hover:underline"
					>
						{review.title ?? review.body?.slice(0, 80) ?? t("noText")}
					</Link>
					{review.title && review.body ? (
						<span className="line-clamp-2 text-xs text-muted-foreground">{review.body}</span>
					) : null}
					<span className="text-xs text-muted-foreground">{review.firstName ?? t("anonymousBuyer")}</span>
					{/* On a phone the other columns are hidden; the stars go under the text. */}
					<span className="md:hidden">
						<Stars stars={review.stars} />
					</span>
				</span>
			),
		},
		{
			id: "stars",
			header: t("column.stars"),
			secondary: true,
			cell: (review) => <Stars stars={review.stars} />,
		},
		{
			id: "product",
			header: t("column.product"),
			secondary: true,
			cell: (review) => (
				<Link
					to="/product-review"
					search={{ ...search, productId: review.product.id, page: 1 }}
					className="underline-offset-4 hover:underline"
				>
					{review.product.name}
				</Link>
			),
		},
		{
			id: "order",
			header: t("column.order"),
			secondary: true,
			cell: (review) => (
				<Link
					to="/order/$orderId"
					params={{ orderId: review.orderId }}
					className="font-mono text-xs underline-offset-4 hover:underline"
				>
					{review.orderNumber}
				</Link>
			),
		},
		{
			id: "status",
			header: t("column.status"),
			cell: (review) => <ReviewStatusBadge status={review.status} />,
		},
		{
			id: "writtenAt",
			header: t("column.written"),
			sortable: true,
			secondary: true,
			cell: (review) => <time dateTime={review.writtenAt}>{formatDateTime(review.writtenAt)}</time>,
		},
	];

	const filtered = Boolean(search.search) || search.status !== undefined || search.productId !== undefined;
	const pastEnd = data.items.length === 0 && data.total > 0;

	return (
		<div className="flex flex-col gap-6">
			<PageHeader title={t("title")} description={t("description")} help="productReview:reviews" />
			<div className="flex flex-col gap-3">
				<DataTableToolbar>
					<SearchInput value={search.search} label={t("searchLabel")} />
					<FilterSelect
						param="status"
						label={t("status.label")}
						value={search.status}
						options={ProductReviewStatus.options.map((status) => ({
							value: status,
							label: t(`status.${status}`),
						}))}
						help="productReview:status"
					/>
					<FilterSelect
						param="productId"
						label={t("product.label")}
						value={search.productId}
						options={data.products.map((product) => ({ value: product.id, label: product.name }))}
					/>
				</DataTableToolbar>
				<DataTable
					label={t("title")}
					columns={columns}
					rows={data.items}
					rowKey={(review) => review.id}
					sort={search.sort}
					order={search.order}
					empty={<DataTableEmpty filtered={filtered} pastEnd={pastEnd} nothingYet={t("empty")} />}
				/>
				<DataTablePagination page={data.page} pageSize={data.pageSize} total={data.total} />
			</div>
		</div>
	);
}
